import { useEffect, useRef } from "react";

// Recompute inert siblings for the top dialog, so nested dialogs can open without
// leaving their own ancestor inert. Preserve any pre-existing inert attributes.
const dialogs = [];
const inertBefore = new Map();
function updateBackground() {
  inertBefore.forEach((value, element) => {
    if (value === null) element.removeAttribute("inert");
    else element.setAttribute("inert", value);
  });
  inertBefore.clear();
  let node = dialogs[dialogs.length - 1]?.element;
  while (node && node !== document.body) {
    const current = node;
    Array.from(node.parentElement?.children || []).forEach((sibling) => {
      if (sibling !== current) {
        inertBefore.set(sibling, sibling.getAttribute("inert"));
        sibling.setAttribute("inert", "");
      }
    });
    node = node.parentElement;
  }
}

function focusableElements(element) {
  return Array.from(element.querySelectorAll(
    'button, a[href], input, select, textarea, iframe, [tabindex]'
  )).filter((node) => !node.disabled && node.tabIndex >= 0 &&
    !node.closest('[hidden], [inert], [aria-hidden="true"]') &&
    getComputedStyle(node).display !== "none" && getComputedStyle(node).visibility !== "hidden");
}

export default function useModalDialog(ref, open, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const element = ref.current;
    if (!open || !element) return undefined;
    const previous = document.activeElement;
    const dialog = { element };
    dialogs.push(dialog);
    updateBackground();
    const focusInitial = () => (element.querySelector("[data-autofocus]") ||
      focusableElements(element)[0] || element).focus();
    focusInitial();
    const isTop = () => dialogs[dialogs.length - 1] === dialog;
    const keydown = (event) => {
      if (!isTop()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current?.();
      }
      if (event.key !== "Tab") return;
      const items = focusableElements(element);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first) { event.preventDefault(); element.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    };
    const focusin = (event) => {
      if (isTop() && !element.contains(event.target)) focusInitial();
    };
    document.addEventListener("keydown", keydown);
    document.addEventListener("focusin", focusin);
    return () => {
      const wasTop = isTop();
      dialogs.splice(dialogs.indexOf(dialog), 1);
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("focusin", focusin);
      updateBackground();
      if (wasTop && previous?.isConnected && !previous.closest("[inert]")) previous.focus();
    };
  }, [ref, open]);
}
